import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Clock,
  ListChecks,
  Search,
  Star,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DiagnosisSearchPanel, { DiagnosisPickList } from "@/components/diagnosis/DiagnosisSearchPanel";
import { ENC } from "@/constants/testIds";

const SOURCE_TABS = [
  { id: "lookup", label: "Lookup", icon: Search },
  { id: "common", label: "Common", icon: Star },
  { id: "prior", label: "Prior", icon: Clock },
  { id: "problem-list", label: "Problem List", icon: ListChecks },
];

export default function EncounterDiagnosisWorkspace({
  selectedCodes = [],
  encounterDiagnoses = [],
  commonDiagnoses = [],
  priorDiagnoses = [],
  problemList = [],
  onAdd,
  onRemove,
  onRemoveAll,
  onSetPrimary,
  onClearPrimary,
  onMove,
  readOnly,
  noteContext,
}) {
  const [sourceTab, setSourceTab] = useState("lookup");
  const selected = new Set(selectedCodes);

  return (
    <div
      data-testid={ENC.screenDiagnosisWorkspace}
      className="relative z-10 grid w-full shrink-0 grid-cols-1 gap-4"
    >
      <div className="min-w-0 space-y-3">
        <section className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-2.5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-900">Diagnosis</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Find a diagnosis, then add it to {noteContext || "this Note"}. Lookup stays open so you
              can add more than one.
            </p>
          </div>

          <div
            data-testid={ENC.screenDiagnosisSourceTabs}
            className="flex flex-wrap gap-1 border-b border-slate-100 px-3 py-1.5"
            role="tablist"
            aria-label="Diagnosis sources"
          >
            {SOURCE_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = sourceTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`${ENC.screenDiagnosisSourceTab}-${tab.id}`}
                  onClick={() => setSourceTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide ${
                    active
                      ? "bg-[#0b1f33] text-white"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="p-4">
            {sourceTab === "lookup" && (
              <DiagnosisSearchPanel
                selectedCodes={selectedCodes}
                onSelect={(row) => onAdd?.(row, "lookup")}
                readOnly={readOnly}
              />
            )}
            {sourceTab === "common" && (
              <SourcePanel
                title="Common Diagnoses"
                detail="Diagnoses you commonly use. This is not the patient's Problem List."
                empty="No common diagnoses are available for this user yet."
                items={commonDiagnoses}
                selected={selected}
                readOnly={readOnly}
                onAdd={(row) => onAdd?.(row, "common")}
                testId={ENC.screenDiagnosisCommon}
              />
            )}
            {sourceTab === "prior" && (
              <SourcePanel
                title="Prior Diagnoses for This Patient"
                detail="Diagnoses from this patient's previous encounters. This is not the Problem List."
                empty="No prior encounter diagnoses were found for this patient."
                items={priorDiagnoses}
                selected={selected}
                readOnly={readOnly}
                onAdd={(row) => onAdd?.(row, "prior")}
                testId={ENC.screenDiagnosisPrior}
              />
            )}
            {sourceTab === "problem-list" && (
              <SourcePanel
                title="Patient Problem List"
                detail="Longitudinal established problems. Selecting a problem adds it to this Note only."
                empty="This patient has no problems on the Problem List yet. Lookup does not add problems automatically."
                items={problemList}
                selected={selected}
                readOnly={readOnly}
                onAdd={(row) => onAdd?.(row, "problem-list")}
                testId={ENC.screenDiagnosisProblemList}
              />
            )}
          </div>
        </section>

        <EncounterDiagnosisTable
          items={encounterDiagnoses}
          readOnly={readOnly}
          onRemove={onRemove}
          onRemoveAll={onRemoveAll}
          onSetPrimary={onSetPrimary}
          onClearPrimary={onClearPrimary}
          onMove={onMove}
        />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-0.5 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Star className="h-3 w-3 fill-amber-400 text-amber-500" />
            Primary
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-slate-400" />
            Secondary
          </span>
          <span>
            Note list only — does not change the ICD-10 master or Problem List. SOAP uses @diagnoses@.
            {noteContext ? ` Diagnoses belong to ${noteContext}.` : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

function SourcePanel({ title, detail, empty, items, selected, readOnly, onAdd, testId }) {
  return (
    <div data-testid={testId} className="space-y-2">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="mt-0.5 text-xs text-slate-500">{detail}</p>
      </div>
      {!items.length ? (
        <p className="py-6 text-center text-sm text-slate-500">{empty}</p>
      ) : (
        <DiagnosisPickList
          items={items}
          selectedCodes={selected}
          onSelect={onAdd}
          readOnly={readOnly}
          countLabel={`${items.length} ${items.length === 1 ? "item" : "items"}`}
        />
      )}
    </div>
  );
}

function EncounterDiagnosisTable({
  items,
  readOnly,
  onRemove,
  onRemoveAll,
  onSetPrimary,
  onClearPrimary,
  onMove,
}) {
  const hasPrimary = items.some((item) => item.isPrimary);

  return (
    <section
      data-testid={ENC.screenEncounterDiagnoses}
      className="overflow-hidden rounded-lg border border-slate-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900">
          Diagnoses
        </h3>
        <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">{items.length}</Badge>
      </div>

      {!items.length ? (
        <p className="px-4 py-6 text-center text-sm text-slate-500">
          No diagnoses are selected for this Note yet.
        </p>
      ) : (
        <div className="overflow-hidden">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-8" />
              <col className="w-[6.5rem]" />
              <col />
              <col className="w-[5.75rem]" />
              <col className="w-[10.75rem]" />
            </colgroup>
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 font-medium">#</th>
                <th className="px-2 py-1.5 font-medium">ICD-10-CM</th>
                <th className="px-2 py-1.5 font-medium">Description</th>
                <th className="px-2 py-1.5 font-medium">Type</th>
                <th className="px-2 py-1.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row, index) => (
                <tr key={row.code} data-testid={`${ENC.screenEncounterDiagnosisRow}-${row.code}`}>
                  <td className="px-2 py-2 text-slate-500">{index + 1}</td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1 font-fhir text-sm font-medium text-sky-800">
                      {row.isPrimary ? (
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                      ) : null}
                      {row.code}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-slate-800 leading-snug">{row.description}</td>
                  <td className="px-2 py-2">
                    {row.isPrimary ? (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Primary</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100">
                        Secondary
                      </Badge>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap items-center gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={readOnly || index === 0}
                        aria-label={`Move ${row.code} up`}
                        data-testid={`${ENC.screenDiagnosisMoveUp}-${row.code}`}
                        onClick={() => onMove?.(row.code, -1)}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={readOnly || index === items.length - 1}
                        aria-label={`Move ${row.code} down`}
                        data-testid={`${ENC.screenDiagnosisMoveDown}-${row.code}`}
                        onClick={() => onMove?.(row.code, 1)}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-1.5 text-[11px]"
                        disabled={readOnly || row.isPrimary}
                        data-testid={`${ENC.screenDiagnosisMakePrimary}-${row.code}`}
                        onClick={() => onSetPrimary?.(row.code)}
                      >
                        Make Primary
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-rose-600"
                        disabled={readOnly}
                        aria-label={`Remove ${row.code}`}
                        data-testid={`${ENC.screenDiagnosisRemove}-${row.code}`}
                        onClick={() => onRemove?.(row.code)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-2 border-t border-slate-100 px-3 py-2.5 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={readOnly || !items.length}
          data-testid={ENC.screenDiagnosisRemoveAll}
          onClick={onRemoveAll}
        >
          Remove All
        </Button>
        <p className="text-center">
          Only one diagnosis can be Primary. Use Make Primary on a row, or Clear Primary.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={readOnly || !hasPrimary}
          data-testid={ENC.screenDiagnosisClearPrimary}
          onClick={onClearPrimary}
        >
          Clear Primary
        </Button>
      </div>
    </section>
  );
}
